import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, ChevronDown, ExternalLink, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import ReviewedByPill from "@/components/ReviewedByPill";
import { trackEvent } from "@/lib/analytics";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { SITE_URL } from "@/lib/seo-constants";
import { SERVICE_LINKS } from "@/components/guides/serviceLinks";

interface RelatedGuide {
  to: string;
  label: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface TocItem {
  id: string;
  label: string;
}

interface GuideLayoutProps {
  title: string;
  metaTitle: string;
  metaDescription: string;
  canonicalPath: string;
  breadcrumbTitle: string;
  /** Breadcrumb parent (defaults to the Guides index). Override for non-guide pages, e.g. commercial spokes under the hub. */
  breadcrumbParentLabel?: string;
  breadcrumbParentTo?: string;
  date?: string;
  ctaTitle?: string;
  ctaDescription?: string;
  ctaButtonText?: string;
  ctaSubtext?: string;
  faqItems?: FaqItem[];
  faqTitle?: string;
  references?: (string | { text: string; url: string })[];
  disclaimer?: string;
  relatedGuides: RelatedGuide[];
  /** Optional direct-answer box shown at the top of the article */
  directAnswer?: string;
  /** Optional quick-facts bullet list shown below the direct answer */
  quickFacts?: string[];
  /** Optional "this route may suit you if" eligibility list (answer-first, safety-forward) */
  suitableFor?: string[];
  /** Heading for the suitableFor box */
  suitableForTitle?: string;
  /** Optional "speak to a GP or clinic instead if" safety-signposting list */
  seeClinicianIf?: string[];
  /** Heading for the seeClinicianIf box */
  seeClinicianTitle?: string;
  /** Optional table of contents items */
  tocItems?: TocItem[];
  /** Optional intro paragraph(s) rendered above the nutshell box */
  intro?: React.ReactNode;
  /** Optional per-guide social share image (absolute URL or site-rooted path, e.g. /og/guides/cerelle-guide.png) */
  ogImage?: string;
  /**
   * Optional "how to get it on the NHS" signpost rendered after the quick facts.
   * A plain, factual route to the service for readers who arrived on a brand or
   * explainer query: who the Pharmacy Contraception Service covers and that
   * supply depends on the pharmacist's assessment. Setting nhsSupplyText turns
   * the block on; the other three have sensible defaults.
   */
  nhsSupplyText?: string;
  nhsSupplyTitle?: string;
  nhsSupplyTo?: string;
  nhsSupplyLabel?: string;
  /**
   * Optional replacement for the closing NHS booking box, for a guide whose next
   * step is somewhere else (the period delay guide shows the Medy signpost).
   * The crawler copy renders it from the page source too.
   */
  cta?: React.ReactNode;
  /** Optional fixed bar, such as a phone-only sticky call to action. Not part of the crawler copy. */
  stickyCta?: React.ReactNode;
  children: React.ReactNode;
}


const GuideLayout = ({
  title,
  metaTitle,
  metaDescription,
  canonicalPath,
  breadcrumbTitle,
  breadcrumbParentLabel = "Guides",
  breadcrumbParentTo = "/guides/",
  date = "March 2026",
  ctaTitle = "Get Your Pill Today",
  ctaDescription = "Completely free and NHS-funded.",
  ctaButtonText = "Start your free consultation",
  ctaSubtext,
  faqItems,
  faqTitle = "Frequently asked questions",
  references,
  disclaimer = "This information is for general guidance only and does not replace advice from a clinician, pharmacist, or doctor. A clinician will review your consultation before any medication is supplied.",
  relatedGuides,
  directAnswer,
  quickFacts,
  suitableFor,
  suitableForTitle = "This service may suit you if",
  seeClinicianIf,
  seeClinicianTitle = "Speak to a GP or sexual health clinic instead if",
  tocItems,
  intro,
  ogImage,
  nhsSupplyText,
  nhsSupplyTitle = "Getting it free on the NHS",
  nhsSupplyTo = "/get-the-pill-online/",
  nhsSupplyLabel = "How to get the pill free on the NHS",
  cta,
  stickyCta,
  children,
}: GuideLayoutProps) => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const { hash } = useLocation();

  // Links into a section (for example from the guides hub search) arrive after a
  // client-side route change, which does not jump to the anchor by itself.
  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const t = window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: "start" }), 60);
    return () => window.clearTimeout(t);
  }, [hash]);

  const canonicalUrl = `${SITE_URL}${canonicalPath.replace(/\/$/, '')}/`;
  const ogImageUrl = ogImage
    ? (ogImage.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`)
    : `${SITE_URL}/og-image.png`;

  // ⚠️ DO NOT add <script type="application/ld+json"> in Helmet.
  // All JSON-LD schema is in static prerendered HTML only.
  // Duplicating it causes Google to reject rich results.

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="en-gb" href={canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background pt-24 pb-16">
        {/* Header area */}
        <div className="bg-[hsl(var(--navy)/0.03)] border-b border-border/40 pb-8 pt-2 mb-0">
          <div className="max-w-2xl mx-auto px-5">
            <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-muted-foreground mb-5 flex-wrap">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to={breadcrumbParentTo} className="hover:text-primary transition-colors">{breadcrumbParentLabel}</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-foreground/70 font-medium">{breadcrumbTitle}</span>
            </nav>

            <h1 className="text-[1.65rem] sm:text-3xl font-bold text-[hsl(var(--navy))] leading-[1.25] mb-5">
              {title}
            </h1>

            <ReviewedByPill date={date} />
          </div>
        </div>

        {/* Article body */}
        <article className="max-w-2xl mx-auto px-5 pt-8">
          <div className="guide-prose prose prose-slate max-w-none

            [&>h2]:!text-[1.35rem] sm:[&>h2]:!text-[1.55rem] prose-h2:leading-snug
            [&>h2]:!font-extrabold prose-h2:tracking-tight [&>h2]:!text-primary
            prose-h2:mt-0 prose-h2:mb-4 prose-h2:pt-0

            prose-h3:text-[1.02rem] prose-h3:sm:text-[1.12rem] prose-h3:leading-snug
            prose-h3:font-bold prose-h3:text-[hsl(var(--navy))]
            prose-h3:mt-0 prose-h3:mb-3 prose-h3:pt-0

            prose-p:text-[0.93rem] prose-p:leading-[1.9] prose-p:text-foreground/72 prose-p:mb-5

            prose-strong:text-foreground/88 prose-strong:font-semibold

            prose-ul:my-4 prose-ul:space-y-2 prose-ul:pl-5
            prose-ol:my-4 prose-ol:space-y-2 prose-ol:pl-5
            prose-li:text-[0.93rem] prose-li:leading-[1.8] prose-li:text-foreground/72

            prose-a:text-primary prose-a:font-medium prose-a:underline prose-a:underline-offset-2
            prose-a:decoration-primary/30 hover:prose-a:decoration-primary

            prose-table:my-6 prose-table:text-[0.875rem] prose-th:text-foreground prose-td:text-foreground/75
            prose-blockquote:border-primary/30 prose-blockquote:text-foreground/60 prose-blockquote:not-italic

            [&>h2]:mt-10 [&>h2]:pt-7 [&>h2]:border-t [&>h2]:border-border/40
            [&>h2:first-of-type]:border-t-0 [&>h2:first-of-type]:mt-6 [&>h2:first-of-type]:pt-0

            [&>h3]:mt-7 [&>h3]:border-l-[3px] [&>h3]:border-primary/30 [&>h3]:pl-3.5

            [&>ul>li::marker]:text-primary/50
            [&>ol>li::marker]:text-primary/60

            [&>p:first-child]:text-[0.97rem] [&>p:first-child]:text-foreground/78 [&>p:first-child]:leading-[1.9]
            [&>p:first-child+p]:text-[0.97rem] [&>p:first-child+p]:text-foreground/78 [&>p:first-child+p]:leading-[1.9]
            [&>p:first-child+p+p]:text-[0.97rem] [&>p:first-child+p+p]:text-foreground/78 [&>p:first-child+p+p]:leading-[1.9]
          ">
            {/* Intro paragraph(s) above the nutshell box */}
            {intro && <div className="mb-6 space-y-5">{intro}</div>}

            {/* Direct answer box */}
            {directAnswer && (
              <div className="not-prose bg-white border border-border/30 rounded-2xl px-4 py-3.5 mb-8 shadow-md shadow-primary/5">
                <p className="text-sm font-bold text-foreground tracking-tight mb-1.5">💡 In a nutshell</p>
                {directAnswer.split('|').map((paragraph, i) => (
                  <p key={i} className="text-[0.88rem] leading-[1.6] text-foreground/70 mb-1.5 last:mb-0">{paragraph}</p>
                ))}
              </div>
            )}

            {/* Quick facts box */}
            {quickFacts && quickFacts.length > 0 && (
              <div className="not-prose bg-card border border-border/50 rounded-xl p-5 mb-8">
                <h2 className="text-base font-bold text-foreground mb-3">Quick facts</h2>
                <ul className="space-y-2">
                  {quickFacts.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-[0.93rem] text-foreground/72 leading-[1.8]">
                      <span className="text-primary mt-1">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Route to the NHS service: a factual signpost, mirrored in the prerender fallback */}
            {nhsSupplyText && (
              <aside aria-labelledby="nhs-supply-heading" className="not-prose rounded-xl border border-primary/25 bg-primary/[0.04] p-5 mb-8">
                <h2 id="nhs-supply-heading" className="text-base font-bold text-foreground mb-2">{nhsSupplyTitle}</h2>
                <p className="text-[0.9rem] leading-[1.75] text-foreground/72 mb-3">{nhsSupplyText}</p>
                <Link
                  to={nhsSupplyTo.replace(/\/$/, "") + "/"}
                  onClick={() => trackEvent("cta_click", { location: canonicalPath, cta: "nhs-supply-top" })}
                  className="inline-flex items-center gap-1 text-[0.9rem] font-semibold text-primary underline underline-offset-2 decoration-primary/30 hover:decoration-primary"
                >
                  {nhsSupplyLabel}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </aside>
            )}

            {/* Eligibility / safety signposting boxes (answer-first, YMYL trust) */}
            {((suitableFor && suitableFor.length > 0) || (seeClinicianIf && seeClinicianIf.length > 0)) && (
              <div className="not-prose grid gap-3 sm:grid-cols-2 mb-8">
                {suitableFor && suitableFor.length > 0 && (
                  <div className="rounded-xl border border-primary/25 bg-primary/[0.04] p-4">
                    <div className="flex items-center gap-2 mb-2.5">
                      <Check className="w-4 h-4 text-primary shrink-0" />
                      <h2 className="text-sm font-bold text-foreground">{suitableForTitle}</h2>
                    </div>
                    <ul className="space-y-1.5">
                      {suitableFor.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-[0.86rem] leading-[1.6] text-foreground/72">
                          <Check className="w-3.5 h-3.5 text-primary/70 mt-0.5 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {seeClinicianIf && seeClinicianIf.length > 0 && (
                  <div className="rounded-xl border border-amber-300/60 bg-amber-50/60 p-4">
                    <div className="flex items-center gap-2 mb-2.5">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                      <h2 className="text-sm font-bold text-foreground">{seeClinicianTitle}</h2>
                    </div>
                    <ul className="space-y-1.5">
                      {seeClinicianIf.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-[0.86rem] leading-[1.6] text-foreground/72">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-500/80 mt-0.5 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Table of contents */}
            {tocItems && tocItems.length > 0 && (
              <div className="not-prose bg-[hsl(var(--navy)/0.03)] border border-border/40 rounded-xl p-5 mb-10">
                <h2 className="text-sm font-bold text-foreground mb-3 uppercase tracking-wider">In this guide</h2>
                <ol className="space-y-1.5">
                  {tocItems.map((item, i) => (
                    <li key={item.id}>
                      <a href={`#${item.id}`} className="text-sm text-foreground/65 hover:text-primary transition-colors">
                        {i + 1}. {item.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {children}
          </div>

          {/* FAQ Section - visible HTML for crawlers and patients */}
          {faqItems && faqItems.length > 0 && (
            <div id="faq" className="mt-14 scroll-mt-28">
              <h2 className="text-[1.35rem] sm:text-[1.55rem] font-bold !text-primary leading-snug tracking-tight mb-6">{faqTitle}</h2>
              <div className="space-y-2.5">
                {faqItems.map((item, i) => {
                  const isOpen = openFaqIndex === i;
                  return (
                    <button
                      key={i}
                      onClick={() => setOpenFaqIndex(isOpen ? null : i)}
                      className={`w-full text-left rounded-2xl transition-all duration-300 overflow-hidden ${
                        isOpen
                          ? 'bg-card shadow-[0_8px_30px_rgb(0,0,0,0.08)] ring-1 ring-primary/15'
                          : 'bg-card shadow-[0_2px_12px_rgb(0,0,0,0.04)] hover:shadow-[0_4px_20px_rgb(0,0,0,0.08)]'
                      }`}
                    >
                      <div className="flex items-center gap-3 px-4 py-3.5">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 transition-colors duration-300 ${
                          isOpen ? 'bg-primary' : 'bg-primary/30'
                        }`} />
                        <span className={`font-medium text-[15px] transition-colors duration-300 flex-1 ${
                          isOpen ? 'text-foreground' : 'text-foreground/90'
                        }`}>
                          {item.question}
                        </span>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                          isOpen ? 'bg-primary/10 rotate-180' : 'bg-muted'
                        }`}>
                          <ChevronDown className={`w-4 h-4 transition-colors duration-300 ${
                            isOpen ? 'text-primary' : 'text-muted-foreground'
                          }`} />
                        </div>
                      </div>
                      <div className={`grid transition-all duration-300 ${
                        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                      }`}>
                        <div className="overflow-hidden">
                          <div className="px-4 pb-4 pt-0">
                            <div className="border-t border-border/50 pt-3">
                              <p className="text-muted-foreground text-sm leading-relaxed pl-5">
                                {item.answer}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Disclaimer */}
          {disclaimer && (
            <p className="mt-10 text-[11px] text-muted-foreground leading-relaxed">
              {disclaimer}
            </p>
          )}

          {/* References */}
          {references && references.length > 0 && (
            <div className="mt-6">
              <Collapsible>
                <div className="bg-card border border-border/50 rounded-xl overflow-hidden">
                  <CollapsibleTrigger className="flex items-center gap-2 group cursor-pointer px-4 py-3 transition-colors hover:bg-muted/40 w-full">
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground transition-transform group-data-[state=open]:rotate-90" />
                    <span className="text-xs font-semibold text-primary uppercase tracking-wider">References ({references.length})</span>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
                    <ol className="space-y-1 text-[11px] text-muted-foreground leading-relaxed list-decimal list-inside px-4 pb-4 pt-1">
                      {references.map((ref, i) => (
                        <li key={i}>
                          {typeof ref === "string" ? ref : (
                            <a href={ref.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 decoration-muted-foreground/40 hover:text-foreground/60 transition-colors">
                              {ref.text}
                            </a>
                          )}
                        </li>
                      ))}
                    </ol>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            </div>
          )}

          {/* Related Guides */}
          {relatedGuides && relatedGuides.length > 0 && (
            <div className="mt-14">
              <h2 className="text-[1.35rem] sm:text-[1.55rem] font-bold !text-primary leading-snug tracking-tight mb-5">Related guides</h2>
              <ul className="space-y-2.5">
                {relatedGuides.map((guide, i) => (
                  <li key={i}>
                    {/* Canonical link form is trailing-slash, normalise so a
                        no-slash entry in a guide's relatedGuides can't regress it. */}
                    <Link to={guide.to.replace(/\/$/, "") + "/"} className="inline-flex items-center gap-1.5 text-[0.93rem] text-foreground/72 hover:text-primary transition-colors underline underline-offset-2 decoration-primary/30 hover:decoration-primary">
                      <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                      {guide.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* CTA */}
          {cta ?? (
            <div className="not-prose mt-14 p-6 sm:p-8 bg-[hsl(var(--navy))] rounded-2xl text-center">
              <h2 className="text-xl font-bold text-white mb-2">{ctaTitle}</h2>
              <p className="text-sm text-white/70 mb-5 max-w-md mx-auto">
                {ctaDescription}
              </p>
              <Link to="/book/" onClick={() => trackEvent("cta_click", { location: canonicalPath, cta: ctaButtonText })}>
                <Button variant="cta" size="lg" className="rounded-full px-8 shadow-lg">
                  {ctaButtonText}
                </Button>
              </Link>
              {ctaSubtext && (
                <p className="text-xs text-white/50 mt-4">{ctaSubtext}</p>
              )}
            </div>
          )}

          {/* About the service: the same four links on every guide */}
          <nav aria-label="About the service" className="not-prose mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm">
            {SERVICE_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-foreground/60 hover:text-primary transition-colors underline underline-offset-2 decoration-primary/20 hover:decoration-primary"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </article>
        {stickyCta}
      </main>

      <Footer />
    </>
  );
};

export default GuideLayout;
